import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { supplierDocumentLineMatchSchema } from "./supplier-document-line-match";

/**
 * Write Supplier Product Matches
 *
 * Consumes the read-only match node's line array and upserts
 * `ap_supplier_products` translation rows (supplier SKU / freeform →
 * ProductVariant) so the next PDF from this vendor auto-matches.
 *
 * Does not write unmatched lines. Existing SupplierProduct rows are left
 * in place except for optional freeform identifier merge.
 */
export const writeSupplierProductMatchesStep = defineStepConfig({
  type: "write_supplier_product_matches",
  name: "Write Supplier Product Matches",
  description:
    "Create or enrich SupplierProduct translations from matched PDF/email lines. Skips unmatched rows. Does not update unit price on existing rows.",
  category: "Data Access",
  libraryModule: "AccountsPayable",

  inputSchema: z.object({
    supplier: entityOfType(SystemEntity.AP_SUPPLIER).describe(
      "Supplier to attach translations to",
    ),
    lines: z
      .array(supplierDocumentLineMatchSchema)
      .describe(
        "Matched lines from match_supplier_document_lines (typically writeCandidates or matchedLines)",
      ),
  }),

  outputSchema: z.object({
    createdCount: z.number().int().nonnegative(),
    updatedCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    createdSupplierProductIds: z.array(z.uuid()),
    updatedSupplierProductIds: z.array(z.uuid()),
  }),

  configSchema: z.object({
    appendFreeformOnExisting: z
      .boolean()
      .prefault(true)
      .describe(
        "When a SupplierProduct already exists for the matched variant, append new extracted identifiers onto supplierFreeform",
      ),
    defaultUnitPrice: z
      .string()
      .regex(/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/)
      .prefault("0")
      .describe(
        "Unit price used when creating a row if the extracted extras have no price. Existing rows are never repriced.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: { maximumAttempts: 2 },
  },
});
